import { useTranslation } from "react-i18next";

// "As featured in" press logo strip. Replace these placeholders with
// the actual outlets/partners you've been featured in. Each entry is
// rendered at ~32px height in grayscale by default and shifts to full
// colour on hover. `href` is optional — when omitted we link to the
// `#press` anchor so the slot still feels real until a URL exists.
export type PressLogo = {
  src: string;
  alt: string;
  href?: string;
  /** Visual width in px so logos with different aspect ratios stay
   * balanced in the strip. Defaults to 140. */
  width?: number;
};

const PRESS_LOGOS: PressLogo[] = [
  {
    src: "/assets/press/realty-daily.svg",
    alt: "Realty Daily",
    width: 140,
    href: "#press",
  },
  {
    src: "/assets/press/proptech-today.svg",
    alt: "PropTech Today",
    width: 160,
    href: "#press",
  },
  {
    src: "/assets/press/bharat-realty.svg",
    alt: "Bharat Realty",
    width: 160,
    href: "#press",
  },
  {
    src: "/assets/press/startup-pulse.svg",
    alt: "Startup Pulse",
    width: 150,
    href: "#press",
  },
  {
    src: "/assets/press/the-urban-post.svg",
    alt: "The Urban Post",
    width: 160,
    href: "#press",
  },
  {
    src: "/assets/press/finance-weekly.svg",
    alt: "Finance Weekly",
    width: 160,
    href: "#press",
  },
];

export default function PressLogos({
  logos = PRESS_LOGOS,
}: { logos?: PressLogo[] }) {
  const { t } = useTranslation();
  const label = t("press.featuredIn", { defaultValue: "Featured in" });
  return (
    <section
      id="press"
      aria-label={label}
      className="bg-white border-y border-border/60"
      data-testid="section-press-logos"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <p
          className="text-center text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-4 sm:mb-6"
          data-testid="text-press-label"
        >
          {label}
        </p>
        <ul
          className="flex flex-nowrap items-center justify-start md:justify-between gap-6 md:gap-4 lg:gap-8 overflow-x-auto md:overflow-visible -mx-4 px-4 md:mx-0 md:px-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          data-testid="list-press-logos"
        >
          {logos.map((logo, i) => {
            const img = (
              <img
                src={logo.src}
                alt={logo.alt}
                width={logo.width ?? 140}
                height={32}
                loading="lazy"
                decoding="async"
                className="h-8 w-auto object-contain grayscale opacity-70 transition duration-200 hover:grayscale-0 hover:opacity-100 max-w-[140px] md:max-w-[100px] lg:max-w-[130px] xl:max-w-[150px]"
              />
            );
            return (
              <li
                key={`${logo.alt}-${i}`}
                className="shrink-0"
                data-testid={`item-press-logo-${i}`}
              >
                {logo.href ? (
                  <a
                    href={logo.href}
                    target={logo.href.startsWith("#") ? undefined : "_blank"}
                    rel={logo.href.startsWith("#") ? undefined : "noopener noreferrer"}
                    aria-label={logo.alt}
                    className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                    data-testid={`link-press-logo-${i}`}
                  >
                    {img}
                  </a>
                ) : (
                  <span aria-label={logo.alt}>{img}</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
